import React, { useEffect, useRef, useState } from "react";

import Conversation from "../routes/ea/Conversation";
import { assistantNameFrom } from "../routes/ea.jsx";
import { get, ENDPOINTS } from "../routes/ea/api";
import "../routes/ea/conversation.css";

/**
 * The persistent Executive Assistant presence (§8.1), made real in #331.
 *
 * SHELL.md: "The user's EA (Donna for the founder) follows them across Cipher
 * as a contextual side dock that understands the current page/offer/filters.
 * It is hidden only on the dedicated EA area itself."
 *
 * ONE CONVERSATION, TWO SURFACES
 * ==============================
 * The dock mounts the same `Conversation` the EA home does, reading the same
 * server transcript. Ask something here, open the EA area, and the exchange is
 * already there — not because two components agree, but because there is one
 * component and one transcript. The same is true of Telegram, which meets this
 * path at `channels.turn()`.
 *
 * WHAT "UNDERSTANDS THE CURRENT PAGE" MEANS, AND DOES NOT
 * =======================================================
 * The dock knows where the operator is standing and SAYS so, so a question
 * asked from the Workstation reads as a question about the Workstation. It
 * does not silently splice that into the prompt: the operator can see the one
 * line of context the assistant is being given. Sending page context invisibly
 * is how a surface starts answering a question nobody asked.
 *
 * TWO THINGS THIS STILL DOES NOT DO, ON PURPOSE
 * =============================================
 * It does not name the assistant from a literal — the name comes from the
 * owner's own setup, and is "your assistant" until they choose one. Donna is
 * the FOUNDER's assistant; every operator has their own.
 *
 * It does not show an "Online" indicator. There is no liveness signal behind
 * it, and a status light that is always green is worse than none. What it does
 * show, once read, is whether the assistant is DORMANT — which is a real state
 * the server reports, not a decoration.
 */
export default function EADock({ user, context }) {
  const [open, setOpen] = useState(false);
  const [shell, setShell] = useState(null);
  const [read, setRead] = useState(false);
  const panelRef = useRef(null);

  // The shell is read when the dock is first opened, not on every page load:
  // a closed dock should cost the operator nothing on a surface they are not
  // using it on.
  useEffect(() => {
    if (!open || read) return undefined;
    let alive = true;
    (async () => {
      const res = await get(ENDPOINTS.myEA);
      if (!alive) return;
      if (res.status === "ok") setShell(res.data?.ea || null);
      setRead(true);
    })();
    return () => { alive = false; };
  }, [open, read]);

  // Escape closes, like every other dismissible layer in the shell.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const fromShell = assistantNameFrom(shell);
  const assistant = fromShell !== "your assistant" ? fromShell : (user?.ea_name || null);
  const name = assistant || "your assistant";
  const label = assistant ? `Ask ${assistant}…` : "Ask your assistant…";

  const where = [context?.area, context?.tab].filter(Boolean).join(" · ") || null;

  if (!open) {
    return (
      <button
        type="button"
        className="ea-dock"
        onClick={() => setOpen(true)}
        aria-label={label}
        aria-expanded="false"
        data-area={context?.area || ""}
        data-tab={context?.tab || ""}
      >
        <span className="ea-orb" aria-hidden="true" />
        <span className="ea-dock-label dim">{label}</span>
      </button>
    );
  }

  return (
    <aside
      className="ea-dock-panel"
      ref={panelRef}
      role="complementary"
      aria-label={`Conversation with ${name}`}
      data-area={context?.area || ""}
      data-tab={context?.tab || ""}
    >
      <header className="ea-dock-head">
        <span className="ea-orb" aria-hidden="true" />
        <span className="ea-dock-title">{name}</span>
        <button
          type="button"
          className="ea-dock-close"
          onClick={() => setOpen(false)}
          aria-label="Close the assistant"
        >
          ×
        </button>
      </header>

      <Conversation
        assistantName={name}
        live={(shell?.lifecycle || "dormant") === "live"}
        compact
        contextLabel={where}
      />
    </aside>
  );
}
